import { computed, type MaybeRefOrGetter, type Ref, ref, toValue, watchEffect } from 'vue'

import type {
  ClientResource,
  ClientsIndex200,
  ClientsIndexParams,
} from '../generated/metapic-api/metapicApi.schemas'
import type {
  FilterCriteriaAsyncFilter,
  LoadClientsForCriteria,
  LoadRevenueTiersForCriteria,
} from '../types/filter-criteria.types'
import type { CamelCased } from '../types/requests.types'

export type Client = CamelCased<ClientResource>

export type ClientsListFilter = {
  advertiserGroupId?: number
  /** Matches client id, client name *and* revenue tier name server-side. */
  query?: string
  size?: number
  sortBy?: string
  page?: number
}

/**
 * `page` is missing from the generated `ClientsIndexParams`: the OpenAPI spec omits it for
 * `clientsIndex`, even though the endpoint paginates — the response `meta` carries `currentPage`
 * and `lastPage`, and eight other generated endpoints document it. Drop this once the spec is fixed.
 */
export type ClientsIndexParamsWithPage = ClientsIndexParams & { page?: number }

const toClientsIndexParams = ({
  advertiserGroupId,
  query,
  size,
  sortBy,
  page,
}: ClientsListFilter): ClientsIndexParamsWithPage => ({
  ...(advertiserGroupId && { 'advertiser_group_ids[]': [advertiserGroupId] }),
  ...(query && { query }),
  ...(size && { size }),
  ...(sortBy && { sort_by: sortBy }),
  ...(page && { page }),
})

export type ClientsApiDeps = {
  listClients: (params: ClientsIndexParamsWithPage) => Promise<CamelCased<ClientsIndex200>>
  useErrorReporter: () => (e: unknown) => void
}

const CRITERIA_OPTIONS_PAGE_SIZE = 50

export const createClientsApi = ({ listClients, useErrorReporter }: ClientsApiDeps) => {
  /**
   * Lists clients through the generated `clientsIndex` operation.
   *
   * Callers pass a plain `advertiserGroupId`; `toClientsIndexParams` above is the single place that
   * knows the endpoint spells it `advertiser_group_ids[]`.
   *
   * `filter` is read inside a `watchEffect`, so any reactive value it touches refetches the list.
   */
  const useClientsList = (
    filter: () => ClientsListFilter,
    { skip }: { skip?: MaybeRefOrGetter<boolean> } = {},
  ): {
    data: Ref<Client[]>
    totalPages: Ref<number>
    isLoading: Ref<boolean>
    error: Ref<Error | null>
  } => {
    const data = ref<Client[]>([])
    const totalPages = ref(0)
    const isLoading = ref(false)
    const error = ref<Error | null>(null)
    const reportError = useErrorReporter()

    /*
     * Typing in a dropdown starts a request per keystroke, and they do not come back in order — a
     * slow early response landing last would otherwise leave the list showing someone else's
     * search. Only the newest request is allowed to write anything, its own error included.
     */
    let latestRequest = 0

    const fetchClients = async (params: ClientsIndexParamsWithPage) => {
      const request = ++latestRequest

      try {
        data.value = []
        isLoading.value = true
        error.value = null

        const response = await listClients(params)

        if (request !== latestRequest) {
          return
        }

        data.value = response.data
        totalPages.value = response.meta.lastPage
      } catch (e: unknown) {
        if (request !== latestRequest) {
          return
        }

        reportError(e)
        error.value = e as Error
      } finally {
        if (request === latestRequest) {
          isLoading.value = false
        }
      }
    }

    watchEffect(() => {
      if (toValue(skip)) {
        return
      }

      void fetchClients(toClientsIndexParams(filter()))
    })

    return { data, totalPages, isLoading, error }
  }

  /**
   * A revenue tier is a child of a client, so both criteria loaders are the same search read two
   * ways rather than two endpoints.
   */
  const createLoadFromClients =
    <T>(advertiserGroupId: Ref<number | undefined>, select: (clients: Client[]) => T[]) =>
    (filterRef: Ref<FilterCriteriaAsyncFilter>) => {
      const { data, isLoading, error } = useClientsList(
        () => ({
          advertiserGroupId: advertiserGroupId.value,
          query: filterRef.value.query,
          size: CRITERIA_OPTIONS_PAGE_SIZE,
        }),
        /*
         * `toClientsIndexParams` drops an undefined group, and the endpoint reads that as "every
         * client there is" rather than "none". A criterion is always scoped to one group, so wait
         * for it instead of asking unscoped.
         */
        { skip: () => advertiserGroupId.value === undefined },
      )

      return { data: computed(() => select(data.value)), isLoading, error }
    }

  const createLoadClientsForCriteria = (
    advertiserGroupId: Ref<number | undefined>,
  ): LoadClientsForCriteria =>
    createLoadFromClients(advertiserGroupId, (clients) =>
      clients.map((client) => ({ id: client.id, label: client.name })),
    )

  const createLoadRevenueTiersForCriteria = (
    advertiserGroupId: Ref<number | undefined>,
  ): LoadRevenueTiersForCriteria =>
    createLoadFromClients(advertiserGroupId, (clients) =>
      clients.flatMap((client) =>
        (client.revenueTiers ?? []).map((tier) => ({
          id: tier.id,
          // Tier names repeat across clients ("Default", "Premium"), so the owner disambiguates them.
          name: `${client.name}: ${tier.name}`,
        })),
      ),
    )

  return { useClientsList, createLoadClientsForCriteria, createLoadRevenueTiersForCriteria }
}
